import { modelUtils } from '@pelicargo/utils';
import { useMemo } from 'react';

import { API } from '../../utils/apiTypes';
import { getRequestPickupOrigin } from '../request/useRequestPickupOrigin';
import { QuoteVariantWithLineItemsPossibleInland } from '../useQuoteVariantHelper';

/** `request` (for the EXW pickup origin) is only selected by the final-quote query. */
export type QuoteFlightPathInput = {
  quote?: API.FinalQuote;
  quoteVariant?: QuoteVariantWithLineItemsPossibleInland;
};

export type UseQuoteFlightPath = {
  flightSegments: API.FinalQuote['flight_path'] | QuoteVariantWithLineItemsPossibleInland['flight_path'];
  pickupOrigin?: string;
  flightPathLabel: string;
};

export const buildFlightPathLabel = (pickupOrigin: string | undefined, airportIds: string[]) => {
  const stops = pickupOrigin ? [pickupOrigin, ...airportIds] : airportIds;
  return stops.filter(Boolean).join(' → ');
};

export const useQuoteFlightPath = ({ quote, quoteVariant }: QuoteFlightPathInput): UseQuoteFlightPath => {
  const flightSegments = useMemo(() => {
    if (quoteVariant?.flight_path?.length) return quoteVariant.flight_path;
    return quote?.flight_path ?? [];
  }, [quote?.flight_path, quoteVariant?.flight_path]);

  const pickupOrigin = useMemo(() => getRequestPickupOrigin(quote?.request), [quote?.request]);

  const flightPathLabel = useMemo(() => {
    const airports = modelUtils.flightSegments.airports(flightSegments);
    return buildFlightPathLabel(
      pickupOrigin,
      airports.map((airport) => airport.id),
    );
  }, [flightSegments, pickupOrigin]);

  return { flightSegments, pickupOrigin, flightPathLabel };
};
